import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { configureStackedBarChartWithoutLegend } from '../../ChartConfiguration/ChartConfiguration';
import { getColorectalCancerScreeningSummary } from '../HedisMetricService';
import ColorectalCancerScreeningSummary from '../../../Models/HedisMetrics/ColorectalCancerScreeningSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { HedisMetricsTypeEnum } from '../../../Enum/HedisMetricsTypeEnum';
import { IconButton, Grid, Paper, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import FilterOptions from '../FilterOptions';
import '../../Widgets/Widgets.css';

const ColorectalCancerScreeningSummaries = (props) => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [colorectalCancerScreeningSummary, setColorectalCancerScreeningSummary] = useState<ColorectalCancerScreeningSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const {
        colorectalCancerScreeningHealthPlans,
        setColorectalCancerScreeningHealthPlans,
        colorectalCancerScreeningTeam,
        setColorectalCancerScreeningTeam,
        colorectalCancerScreeningDateFilter,
        setColorectalCancerScreeningDateFilter,
        colorectalCancerScreeningFromDate,
        setColorectalCancerScreeningFromDate,
        colorectalCancerScreeningToDate,
        setColorectalCancerScreeningToDate,
        colorectalCancerScreeningIncludePastOpenStatus,
        setColorectalCancerScreeningIncludePastOpenStatus,
        colorectalCancerScreeningShowInActiveTeam,
        setColorectalCancerScreeningShowInActiveTeam,
        setHedisType,
        setHealthPlans,
        setTeam,
        setFilter,
        setFDate,
        setTDate,
        setIncludePastOpenStatus,
        setShowInActiveTeam,
        setHasElevatedFilterAccess,
    } = useHedisMetricsContext();
    const chartHeight = 450;

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChartWithoutLegend(
                    'Colorectal Cancer Screening',
                    'Screened vs Not Screened',
                    '',
                    'Count',
                    [{ name: '', data: [0] }],
                    [''],
                    null,
                    chartHeight
                )
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (
            colorectalCancerScreeningDateFilter !== TimePeriodFilterEnum.Custom ||
            (Boolean(colorectalCancerScreeningFromDate) && Boolean(colorectalCancerScreeningToDate))
        ) {
            const changeFilter = async () => {
                let start = colorectalCancerScreeningFromDate !== null ? new Date(colorectalCancerScreeningFromDate).toDateString() : null;
                let end = colorectalCancerScreeningToDate !== null ? new Date(colorectalCancerScreeningToDate).toDateString() : null;

                const data = await getColorectalCancerScreeningSummary(
                    colorectalCancerScreeningHealthPlans,
                    colorectalCancerScreeningTeam,
                    colorectalCancerScreeningDateFilter,
                    start,
                    end,
                    colorectalCancerScreeningIncludePastOpenStatus,
                    colorectalCancerScreeningShowInActiveTeam
                );
                setColorectalCancerScreeningSummary(data);
                setIsPrepared(true);
            };
            if (
                (colorectalCancerScreeningFromDate !== null || colorectalCancerScreeningToDate !== null) &&
                colorectalCancerScreeningToDate < colorectalCancerScreeningFromDate
            ) {
            } else {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Colorectal Cancer Screening',
                        'Screened vs Not Screened',
                        '',
                        'Count',
                        [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
                setIsGraphShow(false);
                changeFilter();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        colorectalCancerScreeningHealthPlans,
        colorectalCancerScreeningTeam,
        colorectalCancerScreeningDateFilter,
        colorectalCancerScreeningFromDate,
        colorectalCancerScreeningToDate,
        colorectalCancerScreeningIncludePastOpenStatus,
        colorectalCancerScreeningShowInActiveTeam,
    ]);

    useEffect(() => {
        if (colorectalCancerScreeningSummary && isPrepared) {
            let xAxisValues = [''];
            let yAxisValues = [];
            let yValues = [];
            let yVal;

            yValues = [];
            yVal = {
                y: colorectalCancerScreeningSummary.Screened,
                name: 'Screened',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Screened', data: yValues, pointPadding: 0, color: '#9ec7e8', showInLegend: true });

            yValues = [];
            yVal = {
                y: colorectalCancerScreeningSummary.NotScreened,
                name: 'Not Screened',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Not Screened', data: yValues, pointPadding: 0, color: '#6495ED', showInLegend: true });

            if (colorectalCancerScreeningSummary.Screened === 0 && colorectalCancerScreeningSummary.NotScreened === 0) {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Colorectal Cancer Screening',
                        'Screened vs Not Screened',
                        '',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
            } else {
                setChartOptions(configureStackedBarChartWithoutLegend('Colorectal Cancer Screening', '', '', 'Count', yAxisValues, xAxisValues, navigateToMembers, chartHeight));
            }

            setIsGraphShow(true);
            setIsPrepared(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colorectalCancerScreeningSummary, colorectalCancerScreeningHealthPlans, isPrepared]);

    const handleClick = (event) => {
        setShow(!show);
    };

    const navigateToMembers = () => {
        setHealthPlans(colorectalCancerScreeningHealthPlans);
        setTeam(colorectalCancerScreeningTeam);
        setFilter(colorectalCancerScreeningDateFilter);
        setFDate(colorectalCancerScreeningFromDate);
        setTDate(colorectalCancerScreeningToDate);
        setIncludePastOpenStatus(colorectalCancerScreeningIncludePastOpenStatus);
        setShowInActiveTeam(colorectalCancerScreeningShowInActiveTeam);
        setHedisType(HedisMetricsTypeEnum.ColorectalCancerScreening);
        setHasElevatedFilterAccess(props.hasElevatedFilterAccess);
        history(`/provider/metricsummary/hedismemberdetail/ColorectalCancerScreening`);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" color={'primary'} onClick={navigateToMembers} className="pointer">
                            Colorectal Cancer Screening
                        </Typography>
                        <Tooltip
                            open={show}
                            title={
                                <div>
                                    <Typography variant="body2">
                                        For members 45-75 years of age, if one of the following criteria is met, the cancer screening metric is considered to be
                                        met
                                    </Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Screened :
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                        Last Met based on the external claim source in the last 2 years
                                    </Typography>
                                    <Typography variant="caption">
                                        CPT 81528 within the measurement year or the 2 years prior to the measurement year
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                        CPT 82270, 82274 or HCPCS G0328 within the measurement year
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                        CPT 45330, 45331, 45332, 45333, 45334, 45335, 45337, 45338, 45340, 45341, 45342, 45346, 45347, 45349, 45350 or HCPCS
                                        G0104 within the measurement year or the 4 years prior to the measurement year
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                        CPT 44388, 44389, 44390, 44391, 44392, 44393, 44394, 44401, 44402, 44403, 44404, 44405, 44406, 44407, 44408, 45378,
                                        45379, 45380, 45381, 45382, 45383, 45384, 45385, 45386, 45387, 45388, 45389, 45390, 45391, 45392, 45393, 45398 or HCPCS
                                        G0105 or G0121 within the measurement year or the 9 years prior to the measurement year
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                        CPT 74261, 74262, 74263 in the measurement year or the 4 years prior to the measurement year
                                    </Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Not Screened :
                                    </Typography>
                                    <Typography variant="caption">No CPT codes recorded</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Exclusions :
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                        ICD10: C18.0, C18.1, C18.2, C18.3, C18.4, C18.5, C18.6, C18.7, C18.8, C18.9, C19, C20, C21.2, C21.8, C78.5, Z85.038,
                                        Z85.048, ODTE0ZZ, ODTE4ZZ, ODTE7ZZ, ODTE8ZZ
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                        CPT: 44150, 44151, 44152, 44153, 44155, 44156, 44157, 44158, 44210, 44211, 44212
                                    </Typography>
                                </div>
                            }
                            PopperProps={{ style: { zIndex: 1000 } }}
                        >
                            <IconButton onClick={handleClick}>
                                <InfoIcon sx={{ width: '18px', height: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <FilterOptions
                        selectedTeam={colorectalCancerScreeningTeam}
                        setSelectedTeam={setColorectalCancerScreeningTeam}
                        dateFilter={colorectalCancerScreeningDateFilter}
                        setDateFilter={setColorectalCancerScreeningDateFilter}
                        fromDate={colorectalCancerScreeningFromDate}
                        setFromDate={setColorectalCancerScreeningFromDate}
                        toDate={colorectalCancerScreeningToDate}
                        setToDate={setColorectalCancerScreeningToDate}
                        healthPlan={colorectalCancerScreeningHealthPlans}
                        setHealthPlan={setColorectalCancerScreeningHealthPlans}
                        includePastOpenStatusMembers={colorectalCancerScreeningIncludePastOpenStatus}
                        setIncludePastOpenStatusMembers={setColorectalCancerScreeningIncludePastOpenStatus}
                        hedisType={HedisMetricsTypeEnum.ColorectalCancerScreening}
                        showInActiveTeams={colorectalCancerScreeningShowInActiveTeam}
                        setShowInActiveTeams={setColorectalCancerScreeningShowInActiveTeam}
                        hasElevatedFilterAccess={props.hasElevatedFilterAccess}
                    />
                    <Grid size={12} sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default ColorectalCancerScreeningSummaries;
